/**
 * @file Renders the modal that confirms the new email with a code.
 */
import { useState } from "react";

import InputExtra from "@components/Form/InputExtra";
import { getConfig } from "@components/Config";
import { TYPE_HIDDEN } from "@components/Form";
import { UpdateModal } from "@components/Page";
import { MODAL_WIDTH2 } from "@components/Constants";
import { useAppContext } from "@components/AppContext";
import type { AppContext } from "@components/AppContext/types";
import { confirmMyIdentityEmailOperation as operation } from "@api/services/identity";

import type { EntityItem, ModalRef } from "../../Page/Modal/types";

const PLATFORM_NAME = getConfig<string>("PLATFORM_NAME");

const LABEL_CODE = "Verification Code";
const LABEL_TITLE = "Email Verification";
const LABEL_ENTITY = "Email";
const LABEL_SUBMIT = "Submit";
const LABEL_SUCCESS = "Your email has been successfully changed";
const LABEL_EMAIL_EXTRA = `A six-digit verification code has been sent to
[EMAIL] by ${PLATFORM_NAME}. Please check your inbox or spam folder to locate
it.`;

type IdentityAppContext = AppContext & {
  resetAuthorization: () => void;
};

type Props = {
  modal: ModalRef;
};

/**
 * Renders the modal that submits the verification code sent to the
 * new email address and resets authorization on success.
 */
const ConfirmEmailModal = ({ modal }: Props): JSX.Element => {
  const { authenticatedRequest: request, resetAuthorization } =
    useAppContext() as IdentityAppContext;

  const [ schema, setSchema ] = useState<unknown[]>([]);

  /**
   * Builds the schema for the confirmation form, embedding the target
   * email and access token as hidden inputs.
   */
  const onOpen = (item: EntityItem) => {
    const email = String(item.email ?? "");

    const newSchema = operation.getSchema([
      {
        name: "email",
        type: TYPE_HIDDEN,
      },
      {
        name: "accessToken",
        type: TYPE_HIDDEN,
      },
      {
        name: "code",
        label: LABEL_CODE,
        minLength: 6,
        maxLength: 6,
        inputProps: {
          style: { width: 180 },
        },
        itemProps: {
          extra: <InputExtra text={LABEL_EMAIL_EXTRA.replace("[EMAIL]", email)} />,
        },
      },
    ]);

    setSchema(newSchema);
  };

  /**
   * Resets authorization so the session picks up the new email.
   */
  const onSuccess = () =>
    resetAuthorization();

  const updateModalProps = {
    width: MODAL_WIDTH2,
    title: LABEL_TITLE,
    entity: LABEL_ENTITY,
    submitLabel: LABEL_SUBMIT,
    successMessage: LABEL_SUCCESS,
    modal,
    schema,
    onOpen,
    request,
    onSuccess,
    operation,
  };

  return (
    <UpdateModal {...updateModalProps} />
  );
};

export default ConfirmEmailModal;
